import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  addDoc
} from 'firebase/firestore';
import { db } from '../config';

// Path: accessories/settings/accessoryTypes
const ACCESSORIES_COLLECTION = 'accessories';
const SETTINGS_DOC = 'settings';
const TYPES_SUBCOLLECTION = 'accessoryTypes';

// Helper function to get the accessory types collection reference
const getAccessoryTypesCollection = () => {
  return collection(db, ACCESSORIES_COLLECTION, SETTINGS_DOC, TYPES_SUBCOLLECTION);
};

export interface AccessoryType {
  id: string;
  category: 'safety' | 'navigation' | 'fishing' | 'recreation' | 'maintenance' | 'comfort' | 'other';
  type: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Create a new accessory type
export const createAccessoryType = async (accessoryType: Omit<AccessoryType, 'id'>): Promise<void> => {
  const typesCollection = getAccessoryTypesCollection();
  const docRef = doc(typesCollection);
  await setDoc(docRef, {
    ...accessoryType,
    createdAt: new Date(),
    updatedAt: new Date(),
  });
};

// Get all accessory types
export const getAccessoryTypes = async (): Promise<AccessoryType[]> => {
  const snapshot = await getDocs(getAccessoryTypesCollection());
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: data.createdAt && typeof data.createdAt.toDate === 'function' 
        ? data.createdAt.toDate() 
        : data.createdAt ? new Date(data.createdAt) : new Date(),
      updatedAt: data.updatedAt && typeof data.updatedAt.toDate === 'function'
        ? data.updatedAt.toDate()
        : data.updatedAt ? new Date(data.updatedAt) : new Date(),
    } as AccessoryType;
  });
};

// Get accessory types by category
export const getAccessoryTypesByCategory = async (category: string): Promise<AccessoryType[]> => {
  const q = query(
    getAccessoryTypesCollection(), 
    where('category', '==', category),
    where('isActive', '==', true),
    orderBy('type', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: data.createdAt && typeof data.createdAt.toDate === 'function' 
        ? data.createdAt.toDate() 
        : data.createdAt ? new Date(data.createdAt) : new Date(),
      updatedAt: data.updatedAt && typeof data.updatedAt.toDate === 'function'
        ? data.updatedAt.toDate()
        : data.updatedAt ? new Date(data.updatedAt) : new Date(),
    } as AccessoryType;
  });
};

// Get active accessory types
export const getActiveAccessoryTypes = async (): Promise<AccessoryType[]> => {
  const q = query(
    getAccessoryTypesCollection(), 
    where('isActive', '==', true),
    orderBy('category', 'asc'),
    orderBy('type', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      createdAt: data.createdAt && typeof data.createdAt.toDate === 'function' 
        ? data.createdAt.toDate() 
        : data.createdAt ? new Date(data.createdAt) : new Date(),
      updatedAt: data.updatedAt && typeof data.updatedAt.toDate === 'function'
        ? data.updatedAt.toDate()
        : data.updatedAt ? new Date(data.updatedAt) : new Date(),
    } as AccessoryType;
  });
};

// Update accessory type
export const updateAccessoryType = async (id: string, accessoryType: Partial<AccessoryType>): Promise<void> => {
  const docRef = doc(getAccessoryTypesCollection(), id);
  await updateDoc(docRef, {
    ...accessoryType,
    updatedAt: new Date(),
  });
};

// Delete accessory type (soft delete by setting isActive to false)
export const deleteAccessoryType = async (id: string): Promise<void> => {
  await updateAccessoryType(id, { isActive: false });
};

// Add a new type if it doesn't exist
export const addAccessoryTypeIfNotExists = async (category: string, type: string): Promise<void> => {
  const existingTypes = await getAccessoryTypesByCategory(category);
  const typeExists = existingTypes.some(t => t.type.toLowerCase() === type.toLowerCase());
  
  if (!typeExists) {
    await createAccessoryType({
      category: category as any,
      type: type,
      isActive: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
  }
};

// Initialize default accessory types
export const initializeDefaultAccessoryTypes = async (): Promise<void> => {
  const defaultTypes = {
    safety: [
      'Adult Life Jacket',
      'Child Life Jacket',
      'Infant Life Jacket',
      'Throwable Cushion',
      'Ring Buoy',
      'Fire Extinguisher',
      'First Aid Kit',
      'Emergency Whistle',
      'Flare Kit',
      'Safety Light'
    ],
    navigation: [
      'Compass',
      'GPS Device',
      'Marine Radio',
      'Charts/Maps',
      'Binoculars',
      'Depth Finder',
      'Navigation Lights',
      'Horn/Sound Signal',
      'Anchor',
      'Anchor Line'
    ],
    fishing: [
      'Fishing Rod',
      'Tackle Box',
      'Fishing Net',
      'Bait Bucket',
      'Cooler',
      'Rod Holder',
      'Fishing Line',
      'Lures/Bait',
      'Fish Finder',
      'Gaff'
    ],
    recreation: [
      'Beach Umbrella',
      'Beach Chair',
      'Water Ski',
      'Wakeboard',
      'Tube/Float',
      'Snorkel Gear',
      'Water Toys',
      'Volleyball Net',
      'Frisbee',
      'Pool Noodles'
    ],
    maintenance: [
      'Bilge Pump',
      'Boat Cover',
      'Fenders',
      'Dock Lines',
      'Cleaning Supplies',
      'Oil/Fluids',
      'Tools',
      'Spare Parts',
      'Battery Charger',
      'Hull Cleaner'
    ],
    comfort: [
      'Boat Cushions',
      'Sunshade/Bimini',
      'Cooler',
      'Cup Holders',
      'Table',
      'Boat Ladder',
      'Swim Platform',
      'Speakers',
      'Portable Toilet',
      'Shower Kit'
    ],
    other: [
      'Storage Box',
      'Rope/Line',
      'Tarp',
      'Bucket',
      'Towels',
      'Boat Hook',
      'Winch',
      'Trailer Parts',
      'Miscellaneous',
      'Custom Item'
    ]
  };

  const existingTypes = await getActiveAccessoryTypes();
  
  // Only initialize if no types exist
  if (existingTypes.length === 0) {
    for (const [category, types] of Object.entries(defaultTypes)) {
      for (const type of types) {
        await createAccessoryType({
          category: category as any,
          type: type,
          isActive: true,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
      }
    }
  }
};

// Get accessory types grouped by category
export const getAccessoryTypesGroupedByCategory = async (): Promise<{ [key: string]: AccessoryType[] }> => {
  const types = await getActiveAccessoryTypes();
  return types.reduce((acc, type) => {
    const category = type.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(type);
    return acc;
  }, {} as { [key: string]: AccessoryType[] });
}; 